import * as Sentry from '@sentry/vue'
import { storeToRefs } from 'pinia'
import { z } from 'zod'
import { useCudaRegistrationCheck } from '~/composables/api/useCudaRegistrationCheck'
import { formatAmount } from '~/composables/offer-utils'
import { isRegistrationAndPaymentEnabled } from '~/composables/registration-payment-cities'
import { translate, useTranslations } from '~/composables/translate/translations'
import { usePrebookingOidcRegistration } from '~/composables/usePrebookingOidcRegistration'
import { usePrebookingValidation } from '~/composables/usePrebookingValidation'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { usePaymentSetupStore } from '~/stores/payment-setup'
import { usePersonalStore } from '~/stores/personal'
import { PrebookingType, usePrebookingCreationStore } from '~/stores/prebooking-creation'
import { useRegistrationFormStore } from '~/stores/registration-form'
import { useStepStore } from '~/stores/step'
import { STEPS_RECORD } from '~/stores/steps-definition'

export function usePrebookingButtonLogic() {
  const paymentSetupStore = usePaymentSetupStore()
  const stepStore = useStepStore()
  const formStore = useRegistrationFormStore()
  const personalStore = usePersonalStore()
  const locationStore = useLocationStore()
  const { checkRegistration } = useCudaRegistrationCheck()
  const { redirectToKeycloakRegistration } = usePrebookingOidcRegistration()
  const { validateNewUserFields, hasNewUserErrors } = usePrebookingValidation()

  const { errorPrebookingCreation } = storeToRefs(usePrebookingCreationStore())

  const isLoading = ref<boolean>(false)

  const isRegistrationPaymentFlowEnabled = computed(() =>
    isRegistrationAndPaymentEnabled(locationStore.city?.locationId),
  )

  const route = useRoute()
  const currentStep = computed(() => STEPS_RECORD[stepStore.currentStepPath!]?.path)
  const isPersonalStep = computed(() => currentStep.value === 'personal')
  const isContactSubView = computed(() => isRegistrationPaymentFlowEnabled.value && isPersonalStep.value && !route.path?.includes('/personal/details'))
  const isDetailsSubView = computed(() => isRegistrationPaymentFlowEnabled.value && isPersonalStep.value && route.path?.includes('/personal/details/'))
  const isPaymentPage = computed(() => isRegistrationPaymentFlowEnabled.value && route.path?.endsWith('/payment'))

  // ── Contact sub-view: run CuDa check and advance to details ────────────────
  async function handleContactContinue() {
    const email = personalStore.emailAddress
    const phone = personalStore.phoneNumber

    // Trigger vee-validate to show field errors for both email and phone
    formStore.setTouched({ email: true, phoneNumber: true })
    await formStore.validatePhoneNumber()

    // Use zod for proper email validation
    const emailResult = z.email().safeParse(email)
    if (!emailResult.success)
      return

    // Phone is required — must have some value (show error if completely empty)
    // Also block if the user typed an invalid phone number (format error shown in ContactForm)
    if (!phone || phone === '' || formStore.phoneNumberFormatInvalid) {
      return // vee-validate / format error already shown via setTouched above
    }

    // Already started Keycloak registration and email/phone unchanged — skip CuDa check
    const emailUnchanged = email === personalStore.checkedEmail
    const phoneUnchanged = phone === personalStore.checkedPhone
    if (personalStore.keycloakRegistrationStarted && emailUnchanged && phoneUnchanged) {
      personalStore.$patch({ isNewUser: true })
      const { $persistPiniaState } = useNuxtApp()
      $persistPiniaState()
      navigateTo(`/${route.params._lang}/${route.params._city}/personal/details/new`)
      return
    }

    isLoading.value = true
    try {
      const { emailExists, phoneExists } = await checkRegistration(email!, phone)
      const isNewUser = !(emailExists || phoneExists)
      personalStore.recordRegistrationCheck(email!, phone, isNewUser)
      const { $persistPiniaState } = useNuxtApp()
      $persistPiniaState()
      const detailsPath = isNewUser ? 'new' : 'existing'
      navigateTo(`/${route.params._lang}/${route.params._city}/personal/details/${detailsPath}`)
    }
    finally {
      isLoading.value = false
    }
  }

  // ── Details sub-view: confirm booking or redirect to Keycloak ──────────────
  async function handleDetailsConfirm() {
    // Run vee-validate on the details form fields (name, country, T&C)
    const isFormValid = await formStore.validateAndCheckForm()

    // Run new-user-specific validation (address, password) separately
    // so errors always show regardless of vee-validate result
    let newUserValid = true
    if (personalStore.isNewUser === true) {
      const newUserErrors = validateNewUserFields()
      personalStore.$patch({ newUserFieldErrors: newUserErrors })
      newUserValid = !hasNewUserErrors(newUserErrors)
    }

    if (!isFormValid || !newUserValid)
      return

    isLoading.value = true

    try {
      if (personalStore.isNewUser === true || personalStore.keycloakRegistrationStarted) {
        const legalEntityId = locationStore.city?.legalEntityId
        if (!legalEntityId) {
          Sentry.captureMessage(`[prebooking-new-user] Missing legalEntityId for city ${String(route.params._city)}`, {
            level: 'error',
            tags: { flow: 'new-user', step: 'details-confirm' },
          })
          isLoading.value = false
          return
        }

        // Check if email and phone unchanged from when they were checked
        const emailUnchanged = personalStore.emailAddress === personalStore.checkedEmail
        const phoneUnchanged = personalStore.phoneNumber === personalStore.checkedPhone

        // Skip Keycloak only if: email AND phone unchanged AND keycloak registration was started (token is in HttpOnly cookie)
        const canSkipKeycloak = personalStore.keycloakRegistrationStarted && emailUnchanged && phoneUnchanged

        if (canSkipKeycloak) {
          await navigateTo(`/${route.params._lang}/${route.params._city}/personal/payment`)
        }
        else {
          personalStore.markKeycloakRegistrationStarted()
          await redirectToKeycloakRegistration(
            {
              email: personalStore.emailAddress!,
              mobilePhone: personalStore.phoneNumber!,
              firstName: personalStore.firstName,
              lastName: personalStore.lastName,
              country: personalStore.countryOfResidence,
              addressStreet: personalStore.addressStreet,
              addressCity: personalStore.addressCity,
              addressZip: personalStore.addressZip,
              password: personalStore.password,
              legalEntityId,
            },
            { lang: route.params._lang as string, city: route.params._city as string },
          )
        }
        // redirectToKeycloakRegistration navigates away
        return
      }

      // Existing user → confirm prebooking directly
      await usePrebookingCreationStore().createPrebooking(PrebookingType.WEB)
    }
    finally {
      isLoading.value = false
    }
  }

  async function onButtonPress() {
    if (!isRegistrationPaymentFlowEnabled.value) {
      if (!isPersonalStep.value) {
        stepStore.goToNextStep()
        return
      }
      const isValid = await formStore.validateAndCheckForm()
      if (!isValid) {
        return
      }
      isLoading.value = true
      try {
        await usePrebookingCreationStore().createPrebooking(PrebookingType.WEB)
      }
      finally {
        isLoading.value = false
      }
      return
    }

    if (isPaymentPage.value) {
      // Payment page → run full Stripe setup sequence then create the prebooking
      isLoading.value = true
      try {
        const paymentAuthorizationId = await paymentSetupStore.performPrepaymentFlow()
        if (paymentAuthorizationId) {
          await usePrebookingCreationStore().createPrebooking(PrebookingType.WEB, paymentAuthorizationId)
          // User already registered via Keycloak — show the registered user success overlay
          if (personalStore.keycloakRegistrationStarted) {
            personalStore.$patch({ isNewCustomer: false })
          }
          await navigateTo(`/${route.params._lang}/${route.params._city}/personal`)
        }
      }
      finally {
        isLoading.value = false
      }
      return
    }
    if (isContactSubView.value) {
      await handleContactContinue()
      return
    }
    if (isDetailsSubView.value) {
      await handleDetailsConfirm()
      return
    }
    if (!isPersonalStep.value) {
      stepStore.goToNextStep()
      return
    }
    isLoading.value = true
    try {
      await usePrebookingCreationStore().createPrebooking(PrebookingType.WEB)
    }
    finally {
      isLoading.value = false
    }
  }

  const offerStore = useOfferStore()
  const extrasStore = useExtrasStore()

  const totalPrice = computed<string | undefined>(() =>
    formatAmount(useTranslations().language.value, (offerStore.selectedOffer?.price ?? 0) + extrasStore.totalExtraPrice, offerStore.selectedOffer?.currency),
  )

  const buttonText = computed<string>(() => {
    if (isRegistrationPaymentFlowEnabled.value && isPaymentPage.value) {
      return translate('sidebar_confirm_for_price').replace('%', totalPrice.value!)
    }
    const buttonTextTemplate = translate('sidebar_continue_wildcard')
    switch (currentStep.value) {
      case 'lead':
        return buttonTextTemplate.replace('%', translate('sidebar_coverage_title'))
      case 'options':
        return buttonTextTemplate.replace('%', translate('sidebar_confirmation'))
      case 'coverage':
        return buttonTextTemplate.replace('%', translate('sidebar_options_title'))
      default:
        locationStore.getLocationConfig()
        if (isRegistrationPaymentFlowEnabled.value) {
          if (isContactSubView.value) {
            return translate('prebooking_continue_to_your_information')
          }
          if (personalStore.isNewUser === true) {
            return translate('prebooking_continue_to_payment')
          }
        }
        return translate('sidebar_confirm_for_price').replace('%', totalPrice.value!)
    }
  })

  return {
    isLoading,
    onButtonPress,
    buttonText,
    isPaymentPage,
    isContactSubView,
    isDetailsSubView,
    isRegistrationPaymentFlowEnabled,
    errorPrebookingCreation,
    paymentSetupStore,
  }
}
